<template>
  <v-container class="pa-0">
    <v-alert v-if="!initialLoading && !hasPhotos" style="background: white;" class="ma-0" outline type="warning">
      <div class="primary--text">
        {{ $t('StationDetailPhotos.noPhotos') }}
      </div>
    </v-alert>
    <v-progress-linear v-if="initialLoading" indeterminate color="info" />
    <v-card>
      <v-card-actions class="card-bg-2 py-2 layout wrap">
        <v-chip class="my-0 ml-0 title" label color="primary" text-color="white">
          {{ `${filteredPhotos.length > 0 ? (photoIndex + 1) : 0} / ${filteredPhotos.length}` }}
          <span v-if="totalCount" class="ml-1 caption">(of {{ totalCount }} total)</span>
        </v-chip>
        <v-spacer />
        <v-btn large icon @click="previous()" :disabled="loading.image">
          <v-icon large>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn large icon @click="next()" :disabled="loading.image">
          <v-icon large>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn color="info" :disabled="!currentPhoto.Url || loading.image" :href="currentPhoto.Url" target="_blank" class="ml-2">
          <v-icon>save_alt</v-icon>
          {{ $t('Button.Download') }}
        </v-btn>
      </v-card-actions>

      <v-layout row wrap>
        <v-flex lg8 md12 xs12>
          <v-alert type="error" v-model="getImgFailed" outline>Failed to get photo.</v-alert>
          <v-layout class="image-container" align-center justify-center>
            <v-progress-circular v-if="initialLoading" size="64" indeterminate color="white" />
            <img v-if="!initialLoading && currentPhoto.Url" :key="currentPhoto.Url" :src="currentPhoto.Url" @load="onload" @error="onerror" class="img-photo" />
            <v-progress-circular v-if="!initialLoading && loading.image && currentPhoto.Url" class="image-progress" large indeterminate color="white" />
          </v-layout>
          <div class="image-info-bar pa-3 body-2">
            <div class="mb-1">{{ currentPhoto.Name }}</div>
            <div class="grey--text text--darken-1">{{ currentPhoto.UploadedAt }}</div>
          </div>

          <!-- Photo Details panel (below photo, left side) -->
          <v-card class="mt-3">
            <v-card-title class="headline pt-2 pb-1">
              Photo Details
              <v-spacer />
              <v-btn v-if="isSystemAdmin && adminPhotoDetail && !showAdminDetail" small color="primary" @click="showAdminDetail = true">
                <v-icon small>edit</v-icon> Edit
              </v-btn>
            </v-card-title>
            <v-card-text>
              <!-- Loading -->
              <v-progress-linear v-if="isSystemAdmin && !adminPhotoDetail && !adminFetchError" indeterminate color="info" class="mb-2" />
              <v-alert v-if="isSystemAdmin && adminFetchError" type="error" outline class="mb-2">{{ adminFetchError }}</v-alert>

              <!-- Admin full detail view -->
              <template v-if="isSystemAdmin && adminPhotoDetail">
                <v-text-field label="Name" :value="adminPhotoDetail.Name" disabled />
                <v-text-field label="Created" :value="formatDate(adminPhotoDetail.CreatedAt, formats.prettyDateFormat)" disabled />
                <v-text-field label="File Size" :value="formatBytes(adminPhotoDetail.FileSizeBytes)" disabled />
                <v-text-field label="Blob URL" :value="adminPhotoDetail.BlobUrl" disabled />
                <v-textarea
                  v-if="showAdminDetail"
                  label="Notes"
                  v-model="adminNotes"
                  :counter="2000"
                  :maxlength="2000"
                  :disabled="adminSaving"
                  rows="3"
                />
                <v-text-field v-else label="Notes" :value="adminPhotoDetail.Notes" disabled />
                <div v-if="showAdminDetail" class="text-xs-right mt-2">
                  <v-btn color="error" outline :disabled="adminDeleting || adminSaving" @click="confirmDelete" class="ml-0">
                    Delete Photo
                  </v-btn>
                  <v-btn color="secondary" :disabled="adminSaving || adminDeleting" @click="showAdminDetail = false">
                    Cancel
                  </v-btn>
                  <v-btn color="primary" :disabled="adminSaving || !adminPhotoDetail" :loading="adminSaving" @click="saveNotes">
                    Save
                  </v-btn>
                </div>
              </template>

              <!-- Non-admin simple view -->
              <template v-if="!isSystemAdmin">
                <div class="mb-2">
                  <div class="body-2">Name</div>
                  <div style="word-break: break-all;">{{ currentPhoto.Name }}</div>
                </div>
                <div>
                  <div class="body-2">Uploaded on</div>
                  <div>{{ currentPhoto.UploadedAt }}</div>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex lg4 md12 xs12 class="divider-left">
          <v-card-title class="headline">Filters</v-card-title>
          <v-card-text class="pt-2">
            <DateRange v-model="dates" startToEndOfDay box :min-max-placeholder="formats.prettyDateFormat" :shortcutPrefix="'Show'" :shortcuts="shortcuts" dataType="photos" no-arrows />
          </v-card-text>
          <v-divider />

          <v-card-title class="headline">Photo List</v-card-title>
          <v-card-text class="pt-0">
            <v-data-table :headers="headers" :items="photoItems" hide-actions>
              <template slot="items" slot-scope="props">
                <tr
                  @click="goToPhoto(props.item)"
                  :style="props.index === photoIndex ? { background: 'rgba(33, 150, 243, 0.15)' } : {}"
                  style="cursor: pointer;">
                  <td>{{ props.index + 1 }}</td>
                  <td>{{ props.item.Name }}</td>
                  <td>{{ props.item.UploadedAt }}</td>
                </tr>
              </template>
            </v-data-table>
            <v-btn v-if="hasMore" block outline color="info" :loading="loadingMore" @click="loadMore" class="mt-2">
              {{ loadingMore ? 'Loading...' : 'Load More' }}
            </v-btn>
          </v-card-text>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteConfirmDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Delete Photo</v-card-title>
        <v-card-text>
          Are you sure you want to permanently delete this photo? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" @click="deleteConfirmDialog = false" :disabled="adminDeleting">Cancel</v-btn>
          <v-btn color="error" :loading="adminDeleting" @click="executeDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import config from '@/../config'
  import localStorageManager from '@/localStorageManager'
  import axios from 'axios'
  import { mapGetters } from 'vuex'

  import {
    prettyDateFormat,
  } from '@/components/mixins/MomentUtil'

  import {
    getPhotosDateTimeBoundary,
  } from '@/helpers/stationPhotos'

  export default {
    props: {
      photos: Array,
      stationName: String,
    },
    data() {
      return {
        loading: {
          image: true,
        },
        loadingMore: false,
        initialLoading: true,
        dates: {
          start: null,
          end: null,
        },
        formats: {
          prettyDateFormat,
        },
        photoIndex: 0,
        getImgFailed: false,
        totalCount: null,
        currentPageIndex: 0,
        pageSize: 10,
        allPhotos: [],
        headers: [
          {
            text: '#',
            sortable: false,
          },
          {
            text: 'Name',
            value: 'Name',
            sortable: false,
          },
          { text: 'Update On', value: 'UploadedAt', sortable: false, },
        ],
        showAdminDetail: false,
        adminPhotoDetail: null,
        adminNotes: '',
        adminSaving: false,
        adminDeleting: false,
        deleteConfirmDialog: false,
        adminFetchError: null,
      }
    },
    computed: {
      ...mapGetters('authenticate', ['isSystemAdmin']),
      boundary() {
        return getPhotosDateTimeBoundary(this.allPhotos)
      },
      hasPhotos() {
        return this.allPhotos && this.allPhotos.length > 0
      },
      hasMore() {
        return this.totalCount == null || this.allPhotos.length < this.totalCount
      },
      isUserDateFilter() {
        return !!(this.dates.start && this.dates.end)
      },
      photoItems() {
        return this.allPhotos.map(photo => ({
          Id: photo.Id,
          Name: photo.FileName,
          UploadedAt: this.formatDate(photo.UploadedAt, prettyDateFormat),
          Moment: moment(photo.UploadedAt),
          Url: this.makePhotoUrl(photo),
        }))
      },
      filteredPhotos() {
        let startDate = this.dates.start
        let endDate = this.dates.end

        if (startDate && endDate) {
          return this.photoItems.filter(photo => photo.Moment.isBetween(startDate, endDate))
        } else if (startDate) {
          return this.photoItems.isAfter(startDate, 'days')
        } else if (endDate) {
          return this.photoItems.isBefore(endDate, 'days')
        } else {
          return this.photoItems
        }
      },
      currentPhoto() {
        return this.filteredPhotos[this.photoIndex] || {}
      },
      shortcuts() {
        return {
          all: {
            name: () => 'All photos',
            hideClear: true,
            make: () => ({
              start: null,
              end: null,
            }),
          },
        }
      },
    },
    watch: {
      stationName: {
        immediate: true,
        handler() {
          if (this.stationName) {
            this.resetAndFetch()
          }
        },
      },
      'currentPhoto.Url'(val, oldVal) {
        if (val != oldVal) this.loading.image = true
      },
      dates: {
        deep: true,
        handler() {
          this.resetAndFetch()
        },
      },
      photoIndex(val, oldVal) {
        if (val > 0 && val >= this.filteredPhotos.length - 10 && this.hasMore && !this.loadingMore) {
          this.loadMore()
        }
        if (this.isSystemAdmin && val !== oldVal) {
          this.showAdminDetail = false
          this.fetchPhotoDetail()
        }
      },
    },
    methods: {
      resetAndFetch() {
        this.initialLoading = true
        this.allPhotos = []
        this.currentPageIndex = 0
        this.totalCount = null
        this.photoIndex = 0
        this.fetchFirstPage()
      },
      async fetchFirstPage() {
        if (!this.stationName) return
        try {
          var encodedName = encodeURIComponent(this.stationName)
          var params = { pageIndex: this.currentPageIndex, pageSize: this.pageSize }
          if (this.isUserDateFilter) {
            params.startTime = this.dates.start.toISOString()
            params.endTime = this.dates.end.toISOString()
          }
          var res = await axios.get('/api/StationPhotos/Paged/' + encodedName, { params })
          var pagedPhotos = res.data.Photos || []
          this.allPhotos = pagedPhotos
          if (res.data.TotalCount != null) this.totalCount = res.data.TotalCount
          this.initialLoading = false
          if (this.isSystemAdmin && this.allPhotos.length > 0) {
            this.fetchPhotoDetail()
          }
        } catch (e) {
          console.error('Failed to fetch photos', e)
          this.initialLoading = false
        }
      },
      // customSort (items, index, isDesc) {
      //   items.sort((a, b) => {
      //     if (index=='uploadedAt') {
      //       if (!isDesc) {
      //         return new Date(b[index]) - new Date(a[index])
      //       } else {
      //         return new Date(a[index]) - new Date(b[index])
      //       }
      //     }
      //     else {
      //       if(typeof a[index] !== 'undefined'){
      //         if (!isDesc) {
      //           return a[index].toLowerCase().localeCompare(b[index].toLowerCase())
      //         }
      //         else {
      //           return b[index].toLowerCase().localeCompare(a[index].toLowerCase())
      //         }
      //       }
      //     }
      //   })
      //   return items
      // },
      async loadMore() {
        if (!this.stationName || this.loadingMore || !this.hasMore) return
        this.loadingMore = true
        this.currentPageIndex++
        try {
          let encodedName = encodeURIComponent(this.stationName)
          let params = {
            pageIndex: this.currentPageIndex,
            pageSize: this.pageSize,
          }
          if (this.isUserDateFilter) {
            params.startTime = this.dates.start.toISOString()
            params.endTime = this.dates.end.toISOString()
          }
          let res = await axios.get(`/api/StationPhotos/Paged/${encodedName}`, { params })
          let newPhotos = res.data.Photos || []
          var existingNames = new Set(this.allPhotos.map(function (p) { return p.FileName }))
          var deduped = newPhotos.filter(function (p) { return !existingNames.has(p.FileName) })
          if (deduped.length > 0) {
            this.allPhotos = [...this.allPhotos, ...deduped]
          }
          if (res.data.TotalCount != null) this.totalCount = res.data.TotalCount
        } catch (e) {
          console.error('Failed to load more photos', e)
        } finally {
          this.loadingMore = false
        }
      },
      onload() {
        this.loading.image = false
        this.getImgFailed = false
      },
      onerror() {
        this.getImgFailed = true
        this.loading.image = false
      },
      formatDate(date, format) {
        return moment(date).format(format)
      },
      makePhotoUrl(photo) {
        let url = photo.FileUrl
        if (config.url != '/token') {
          let authorizationHeader = localStorageManager.getItem('access_token')
          return `${url}?t=${authorizationHeader}`
        }
        return url
      },
      goToPhoto(photoItem) {
        var index = this.photoItems.indexOf(photoItem)
        if (index >= 0) {
          this.photoIndex = index
        }
      },
      async fetchPhotoDetail() {
        var photo = this.filteredPhotos[this.photoIndex]
        if (!photo || !photo.Id) return
        this.adminFetchError = null
        this.adminPhotoDetail = null
        try {
          var res = await axios.get('/api/StationPhotos/' + photo.Id)
          this.adminPhotoDetail = res.data
          this.adminNotes = res.data.Notes || ''
        } catch (e) {
          this.adminFetchError = 'Failed to load photo details.'
          console.error('Failed to fetch photo detail', e)
        }
      },
      async saveNotes() {
        if (!this.adminPhotoDetail) return
        this.adminSaving = true
        try {
          var res = await axios.patch('/api/StationPhotos/' + this.adminPhotoDetail.Id, {
            notes: this.adminNotes
          })
          if (this.adminPhotoDetail) {
            this.adminPhotoDetail.Notes = res.data.Notes
          }
          this.$store.commit('app/setSnackbar', { message: 'Notes saved.', color: 'success' })
        } catch (e) {
          console.error('Failed to save notes', e)
          this.$store.commit('app/setSnackbar', { message: 'Failed to save notes.', color: 'error' })
        } finally {
          this.adminSaving = false
        }
      },
      confirmDelete() {
        this.deleteConfirmDialog = true
      },
      async executeDelete() {
        console.log(this.adminPhotoDetail)
        if (!this.adminPhotoDetail) return
        this.adminDeleting = true
        try {
          await axios.delete('/api/StationPhotos/' + this.adminPhotoDetail.Id)
          var photoId = this.adminPhotoDetail.Id
          this.allPhotos = this.allPhotos.filter(function (p) { return p.Id !== photoId })
          this.showAdminDetail = false
          this.deleteConfirmDialog = false
          if (this.photoIndex >= this.filteredPhotos.length) {
            this.photoIndex = Math.max(0, this.filteredPhotos.length - 1)
          }
          this.$store.commit('app/setSnackbar', { message: 'Photo deleted.', color: 'success' })
        } catch (e) {
          console.error('Failed to delete photo', e)
          this.$store.commit('app/setSnackbar', { message: 'Failed to delete photo.', color: 'error' })
        } finally {
          this.adminDeleting = false
        }
      },
      formatBytes(bytes) {
        if (!bytes || bytes === 0) return '0 Bytes'
        var k = 1024
        var sizes = ['Bytes', 'KB', 'MB', 'GB']
        var i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
      },
      next() {
        if (this.photoIndex < this.filteredPhotos.length - 1) this.photoIndex += 1
        else this.photoIndex = 0
      },
      previous() {
        if (this.photoIndex > 0) this.photoIndex -= 1
        else this.photoIndex = this.filteredPhotos.length - 1
      },
    },
  }
</script>
<style scoped>
  .image-container {
    background: black;
    height: 600px;
    position: relative;
  }

  .image-progress {
    position: absolute;
    left: 50%;
    top: 50%;
  }

  .img-photo {
    -ms-interpolation-mode: bicubic;
    object-fit: contain;
    height: 100%;
    width: 100%;
  }

  .image-info-bar {
    background: #f5f5f5;
    border-bottom: 1px solid rgba(0,0,0,.12);
  }

</style>
